import { logger } from "../utils/logger";
import { useState, useEffect, lazy, Suspense } from 'react';
import { Link } from 'react-router';
import { Helmet } from 'react-helmet';
import { 
  Sparkles, 
  Star, 
  Award, 
  Target, 
  Check, 
  CheckCircle2, 
  Users, 
  Globe2, 
  Shield, 
  Briefcase, 
  TrendingUp, 
  Zap, 
  Clock, 
  Rocket, 
  Video, 
  Boxes, 
  Cog, 
  BarChart3, 
  FileText, 
  DollarSign,
  ArrowRight,
  Settings
} from 'lucide-react';
import { Button } from '../components/ui/button';

// Eagerly loaded — used in critical render path
import { TrustedClients } from '../components/sections/TrustedClients';
import { WhyChooseUsCreative } from '../components/sections/WhyChooseUsCreative';
import { BundleOfferCampaign } from '../components/BundleOfferCampaign';
import { ServicesGrid } from '../components/ServicesGrid';
import { ROIMiniTeaserPremium } from '../components/ROIMiniTeaserPremium';
import { HowWeWorkSection } from '../components/HowWeWorkSection';
import { IndustryExpertiseSection } from '../components/IndustryExpertiseSection';
import { BlogPreview } from '../components/sections/BlogPreview';
import { VideoPlanner } from '../components/sections/VideoPlanner';
import { FAQSection } from '../components/sections/FAQSection';

// Lazy loaded — heavy components not needed on first paint
const CustomVideoPlayer  = lazy(() => import('../components/CustomVideoPlayer').then(m => ({ default: m.CustomVideoPlayer })));
const VideoPlannerModal  = lazy(() => import('../components/VideoPlannerModal').then(m => ({ default: m.VideoPlannerModal })));
const VideoModal         = lazy(() => import('../components/VideoModal').then(m => ({ default: m.VideoModal })));
const PDFDesignPreview   = lazy(() => import('../components/PDFDesignPreview').then(m => ({ default: m.PDFDesignPreview })));

import { extractYouTubeId } from '../utils/extractYouTubeId';
import { homepageAPI } from '../utils/api';
import { API_URL } from '../utils/api';

// Icon map for dynamic hero badge icon rendering
const iconMap: { [key: string]: any } = {
  Sparkles, Star, Award, Target, Check, CheckCircle2, Users, Globe2, Shield,
  Briefcase, TrendingUp, Zap, Clock, Rocket, Video, Boxes, Cog, BarChart3,
  FileText, DollarSign
};

// ============================================
// SKELETON COMPONENTS
// ============================================

const HeroSkeleton = () => (
  <div className="animate-pulse">
    <div className="h-8 bg-gray-200 rounded-full mb-6 w-56"></div>
    <div className="h-12 bg-gray-200 rounded mb-3 w-full"></div>
    <div className="h-12 bg-gray-200 rounded mb-3 w-4/5"></div>
    <div className="h-12 bg-gray-200 rounded mb-6 w-3/5"></div>
    <div className="h-5 bg-gray-200 rounded mb-2 w-full"></div>
    <div className="h-5 bg-gray-200 rounded mb-6 w-2/3"></div>
    <div className="flex gap-3 mb-6">
      <div className="h-11 bg-gray-200 rounded-full w-40"></div>
      <div className="h-11 bg-gray-200 rounded-full w-40"></div>
    </div>
    <div className="flex gap-2 items-center">
      <div className="h-4 bg-gray-200 rounded w-24"></div>
      <div className="h-4 bg-gray-200 rounded w-32"></div>
    </div>
  </div>
);

const VideoPlayerSkeleton = () => (
  <div className="animate-pulse">
    <div className="aspect-video bg-gray-200 rounded-2xl w-full"></div>
  </div>
);

const SectionSkeletons = () => (
  <div className="space-y-6 px-4 sm:px-6 lg:px-8 py-8">
    {/* Simulates a full-width section block */}
    {[180, 240, 200, 160, 220].map((h, i) => (
      <div
        key={i}
        className="w-full bg-gray-100 rounded-2xl relative overflow-hidden animate-pulse"
        style={{ height: h }}
      >
        <div className="absolute inset-0 shimmer" />
      </div>
    ))}
  </div>
);

// ============================================
// INTERFACES
// ============================================

interface HeroSection {
  id: string;
  badgeText: string;
  badgeIcon: string;
  headlineLine1: string;
  headlineLine2: string;
  headlineLine3Gradient: string;
  description: string;
  cta1Text: string;
  cta1Link: string;
  cta1Visible: boolean;
  cta2Text: string;
  cta2Link: string;
  cta2Visible: boolean;
  videoUrl: string;
  videoAutoplay: boolean;
  videoDelay: number;
  stat1Value: string;
  stat1Label: string;
  stat2Value: string;
  stat2Label: string;
  statsVisible: boolean;
  ratingValue: number;
  ratingText: string;
  ratingVisible: boolean;
  updatedAt: string;
}

interface WhyChooseSection {
  id: string;
  title: string;
  description: string;
  points: { title: string, description: string }[];
  updatedAt: string;
}

interface ServiceItem {
  id: string;
  title: string;
  description: string;
  icon: string;
  link: string;
  videoUrl: string;
  videoId: string;
  gradient: string;
  overlayEnabled: boolean;
}

interface ServicesSection {
  id: string;
  badgeText: string;
  badgeIcon: string;
  title: string;
  description: string;
  items: ServiceItem[];
  visible: boolean;
  updatedAt: string;
}

interface ImpactStat {
  id: string;
  value: string;
  label: string;
  description: string;
  icon: string;
  gradient: string;
  highlight: boolean;
}

interface RealWorldImpactSection {
  id: string;
  badgeText: string;
  badgeIcon: string;
  title: string;
  subtitle: string;
  stats: ImpactStat[];
  ctaText: string;
  ctaLink: string;
  visible: boolean;
  updatedAt: string;
}

export function HomePage() {
  // Default fallback hero data
  const defaultHeroData: HeroSection = {
    id: 'default',
    badgeText: '🎬 Award-Winning Animation Studio',
    badgeIcon: 'Sparkles',
    headlineLine1: 'Transform Complex Ideas',
    headlineLine2: 'Into Powerful Visual Stories',
    headlineLine3Gradient: 'That Drive Results',
    description: 'We create premium 3D animations and explainer videos that help B2B companies in manufacturing, energy, and automotive industries communicate complex technical concepts with clarity and impact.',
    cta1Text: 'Start Your Project',
    cta1Link: '/contact',
    cta1Visible: true,
    cta2Text: 'Calculate Your ROI',
    cta2Link: '/roi-calculator',
    cta2Visible: true,
    videoUrl: 'https://www.youtube.com/watch?v=QBM3QFhmySU',
    videoAutoplay: true,
    videoDelay: 2000,
    stat1Value: '280+',
    stat1Label: 'Videos Delivered',
    stat2Value: '98%',
    stat2Label: 'Client Satisfaction',
    statsVisible: true,
    ratingValue: 4.9,
    ratingText: 'from 180+ client reviews',
    ratingVisible: true,
    updatedAt: new Date().toISOString(),
  };

  const [heroSection, setHeroSection] = useState<any>(null);
  const [heroLoading, setHeroLoading] = useState(true);
  const [servicesData, setServicesData] = useState<any>(null);
  const [servicesLoading, setServicesLoading] = useState(true);
  const [impactSection, setImpactSection] = useState<any>(null);
  const [impactLoading, setImpactLoading] = useState(true);
  const [failedThumbnails, setFailedThumbnails] = useState(new Set());
  
  // Homepage settings for SEO
  const [homepageSettings, setHomepageSettings] = useState<any>(null);
  
  // Section order state
  const [sectionOrder, setSectionOrder] = useState<any[]>([]);
  const [orderLoading, setOrderLoading] = useState(true);

  // Modal states
  const [isPlannerModalOpen, setIsPlannerModalOpen] = useState(false);
  const [videoModalOpen, setVideoModalOpen] = useState(false);
  const [currentVideo, setCurrentVideo] = useState({ url: '', title: '' });
  const [pdfPreviewOpen, setPdfPreviewOpen] = useState(false);

  useEffect(() => {
    Promise.all([
      fetchHeroSection(),
      fetchServicesSection(),
      fetchRealWorldImpactSection(),
      fetchSectionOrder(),
      fetchHomepageSettings()
    ]).catch(error => {
      logger.error('❌ [HOMEPAGE] Error loading sections:', error);
    });
    
    const handleVisibilityChange = () => {
      fetchSectionOrder();
    };
    
    window.addEventListener('sectionVisibilityChanged', handleVisibilityChange);
    
    const pollInterval = setInterval(() => {
      if (window.location.hash.includes('/admin/homepage')) {
        Promise.all([
          fetchHeroSection(),
          fetchServicesSection(),
          fetchRealWorldImpactSection(),
          fetchSectionOrder()
        ]).catch(error => {
          logger.error('❌ [POLLING] Error during auto-refresh:', error);
        });
      }
    }, 60000);
    
    return () => {
      window.removeEventListener('sectionVisibilityChanged', handleVisibilityChange);
      clearInterval(pollInterval);
    };
  }, []);

  const fetchHeroSection = async () => {
    try {
      const result = await homepageAPI.getHero();
      if (result.success && result.data) {
        setHeroSection(result.data);
      }
    } catch (error: any) {
      logger.warn('⚠️ [HERO SECTION] Error:', error.message);
    } finally {
      setHeroLoading(false);
    }
  };

  const fetchServicesSection = async () => {
    try {
      const response = await fetch(`${API_URL}/homepage/services`, {
        headers: { 'Content-Type': 'application/json' },
      });
      const result = await response.json();
      if (result.success && result.data) {
        setServicesData(result.data);
      }
    } catch (error: any) {
      logger.warn('⚠️ [SERVICES] Error:', error.message);
    } finally {
      setServicesLoading(false);
    }
  };

  const fetchRealWorldImpactSection = async () => {
    try {
      const response = await fetch(`${API_URL}/homepage/real-world-impact`, {
        headers: { 'Content-Type': 'application/json' },
      });
      const result = await response.json();
      if (result.success && result.data) {
        setImpactSection(result.data);
      }
    } catch (error: any) {
      logger.warn('⚠️ [IMPACT] Error:', error.message);
    } finally {
      setImpactLoading(false);
    }
  };

  const fetchSectionOrder = async () => {
    try {
      const result = await homepageAPI.getSectionOrder();
      logger.log('📦 [SECTION ORDER] Raw API response:', JSON.stringify(result, null, 2));
      logger.log('📦 [SECTION ORDER] Array.isArray(result.data):', Array.isArray(result.data));

      if (!result || !result.success || !result.data) {
        logger.warn('⚠️ [SECTION ORDER] Backend not configured yet - using default section order');
        setSectionOrder([
          { id: 'hero', name: 'Hero', visible: true, order: 1 },
          { id: 'logos', name: 'Trusted Clients', visible: true, order: 2 },
          { id: 'why-choose', name: 'Why Choose Us', visible: true, order: 3 },
          { id: 'services', name: 'Services', visible: true, order: 4 },
          { id: 'real-world-impact', name: 'Real World Impact', visible: true, order: 5 },
          { id: 'bundle-pricing', name: 'Bundle Pricing', visible: true, order: 6 },
        ]);
        return;
      }
      
      let sectionsArray = result.data;
      
      if (!Array.isArray(sectionsArray) && typeof sectionsArray === 'object' && sectionsArray.sections) {
        sectionsArray = sectionsArray.sections;
      }

      logger.log('📦 [SECTION ORDER] Array.isArray(sectionsArray):', Array.isArray(sectionsArray));
      
      if (!Array.isArray(sectionsArray)) {
        logger.error('❌ [SECTION ORDER] Data is not an array!');
        setSectionOrder([]);
        return;
      }
      
      if (sectionsArray.length === 0) {
        logger.warn('⚠️ [SECTION ORDER] Empty array returned from backend');
        setSectionOrder([]);
        return;
      }
      
      const transformedSections = sectionsArray.map((item: any, index: number) => {
        if (typeof item === 'object' && item.id) {
          return {
            ...item,
            visible: item.visible !== undefined ? item.visible : true,
            order: item.order || index + 1
          };
        }
        if (typeof item === 'string') {
          return {
            id: item,
            name: item.charAt(0).toUpperCase() + item.slice(1).replace(/-/g, ' '),
            visible: true,
            order: index + 1
          };
        }
        logger.warn('⚠️ [SECTION ORDER] Unknown section format:', item);
        return {
          id: `section-${index}`,
          name: `Section ${index + 1}`,
          visible: true,
          order: index + 1
        };
      });

      setSectionOrder(transformedSections);
      
    } catch (error: any) {
      logger.error('❌ [SECTION ORDER] Exception caught:', error);
      setSectionOrder([]);
    } finally {
      setOrderLoading(false);
    }
  };

  const fetchHomepageSettings = async () => {
    try {
      const response = await fetch(`${API_URL}/homepage/settings`, {
        headers: { 'Content-Type': 'application/json' },
      });
      const result = await response.json();
      if (result.success && result.data) {
        setHomepageSettings(result.data);
      }
    } catch (error: any) {
      logger.warn('⚠ [HOMEPAGE SETTINGS] Error:', error.message);
    }
  };

  const heroVideoId = heroSection?.videoUrl ? extractYouTubeId(heroSection.videoUrl) : null;
  
  const openVideoModal = (videoUrl: string, title: string) => {
    setCurrentVideo({ url: videoUrl, title });
    setVideoModalOpen(true);
  };

  const handleThumbnailError = (index: number) => {
    setFailedThumbnails(prev => new Set(prev).add(index));
  };

  // Component mapping for dynamic section rendering
  const sectionComponents: { [key: string]: JSX.Element | null } = {
    'hero': null,
    'logos': <TrustedClients key="logos" />,
    'why-choose': <WhyChooseUsCreative key="why-choose" />,
    'bundle-pricing': <BundleOfferCampaign key="bundle-pricing" />,
    'services': (
      <section key="services" className="relative py-12 sm:py-16 lg:py-20 px-4 sm:px-6 lg:px-8 bg-gradient-to-br from-gray-50 via-white to-gray-100 overflow-hidden">
        <div className="absolute inset-0 opacity-5">
          <div className="absolute top-0 right-0 w-[600px] h-[600px] bg-gradient-to-bl from-[#2F2E5C] via-[#4a487a] to-transparent rounded-full blur-3xl" />
          <div className="absolute bottom-0 left-0 w-[600px] h-[600px] bg-gradient-to-tr from-[#D74444] via-[#e85e5e] to-transparent rounded-full blur-3xl" />
        </div>
        <div className="absolute inset-0 opacity-[0.01]" style={{backgroundImage: 'url("data:image/svg+xml,%3Csvg viewBox=\'0 0 400 400\' xmlns=\'http://www.w3.org/2000/svg\'%3E%3Cfilter id=\'noiseFilter\'%3E%3CfeTurbulence type=\'fractalNoise\' baseFrequency=\'0.9\' numOctaves=\'4\' /%3E%3C/filter%3E%3Crect width=\'100%25\' height=\'100%25\' filter=\'url(%23noiseFilter)\' /%3E%3C/svg%3E")'}} />
        
        <div className="max-w-7xl mx-auto relative z-10">
          <div className="text-center mb-12">
            <div className="inline-flex items-center gap-2 bg-white/60 backdrop-blur-xl px-4 py-2 rounded-full mb-4 border border-white/40 shadow-lg">
              {(() => {
                const BadgeIcon = servicesData ? iconMap[servicesData.badgeIcon] : Sparkles;
                return BadgeIcon ? <BadgeIcon className="w-4 h-4 text-[#E87D3E]" /> : <Sparkles className="w-4 h-4 text-[#E87D3E]" />;
              })()}
              <span className="text-sm font-semibold bg-gradient-to-r from-[#E87D3E] to-[#E8B84D] bg-clip-text text-transparent">
                {servicesData ? servicesData.badgeText : 'Our Services'}
              </span>
            </div>
            
            <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold mb-4 text-[#2F2E5C]">
              {servicesData ? servicesData.title : '2D & 3D Animation Services'}
            </h2>
            <p className="text-base text-gray-600 max-w-2xl mx-auto leading-relaxed">
              {servicesData ? servicesData.description : 'From concept visualization to full-scale product demos, every animation is designed to drive specific business outcomes.'}
            </p>
          </div>
          
          {servicesData && servicesData.visible && servicesData.items.length > 0 ? (
            <ServicesGrid items={servicesData.items} onVideoClick={openVideoModal} />
          ) : !servicesLoading && (
            <ServicesGrid 
              items={[
                { id: '1', title: "Product Feature Demos", description: "Showcase every function, detail, and movement of your product in stunning 3D.", icon: 'Boxes', link: "/services/product-demos", videoUrl: "https://youtu.be/QBM3QFhmySU", videoId: "QBM3QFhmySU", gradient: "from-[#E87D3E] to-[#E8B84D]", overlayEnabled: true },
                { id: '2', title: "Exploded & Assembly Animations", description: "Ideal for training, manufacturing, and technical explanations.", icon: 'Cog', link: "/services/training-safety", videoUrl: "https://youtu.be/6qSHwzpaVoY", videoId: "6qSHwzpaVoY", gradient: "from-[#D74444] to-[#e85e5e]", overlayEnabled: true },
                { id: '3', title: "Process & Workflow Animations", description: "Demonstrate how your product works within a system or workflow.", icon: 'BarChart3', link: "/services/process-visualization", videoUrl: "https://youtu.be/2_FQMxUUoIE", videoId: "2_FQMxUUoIE", gradient: "from-[#E8B84D] to-[#E87D3E]", overlayEnabled: true },
                { id: '4', title: "2D Explainer Videos", description: "Perfect for software demos, marketing campaigns, and concept communication.", icon: 'FileText', link: "/videos", videoUrl: "https://youtu.be/ndaPT-uOhFg", videoId: "ndaPT-uOhFg", gradient: "from-[#2F2E5C] to-[#4a487a]", overlayEnabled: true },
                { id: '5', title: "Training & Safety Videos", description: "Reduce training time and improve safety compliance with visual learning.", icon: 'Shield', link: "/services/training-safety", videoUrl: "https://youtu.be/D7A20Vsir3E", videoId: "D7A20Vsir3E", gradient: "from-[#e85e5e] to-[#D74444]", overlayEnabled: true },
                { id: '6', title: "Sales & Marketing Animation", description: "Close deals faster with compelling visual storytelling that resonates.", icon: 'Target', link: "/videos", videoUrl: "https://youtu.be/4RlZtlYbUUk", videoId: "4RlZtlYbUUk", gradient: "from-[#E87D3E] to-[#E8B84D]", overlayEnabled: true }
              ]} 
              onVideoClick={openVideoModal} 
            />
          )}
        </div>
      </section>
    ),
    'real-world-impact': (
      <section key="real-world-impact" className="relative py-16 sm:py-20 lg:py-24 px-4 sm:px-6 lg:px-8 bg-[#0a0a1f] text-white overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-[#2F2E5C] via-[#1a1935] to-[#0a0a1f]" />
        <div className="absolute inset-0">
          <div className="absolute top-0 left-0 w-[600px] h-[600px] bg-gradient-to-br from-[#D74444] via-[#e85e5e] to-[#ff6b6b] opacity-20 rounded-full blur-3xl animate-pulse" style={{animationDuration: '4s'}} />
          <div className="absolute bottom-0 right-0 w-[700px] h-[700px] bg-gradient-to-tl from-[#E8B84D] via-[#E87D3E] to-[#ffa94d] opacity-20 rounded-full blur-3xl" />
          <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[500px] h-[500px] bg-gradient-to-r from-[#4a487a] via-[#6b68a8] to-[#4a487a] opacity-15 rounded-full blur-3xl" />
        </div>
        <div className="absolute inset-0 bg-gradient-radial from-transparent via-transparent to-[#0a0a1f]/60" />
        <div className="absolute inset-0 opacity-[0.03]" style={{backgroundImage: 'url("data:image/svg+xml,%3Csvg viewBox=\'0 0 400 400\' xmlns=\'http://www.w3.org/2000/svg\'%3E%3Cfilter id=\'noiseFilter\'%3E%3CfeTurbulence type=\'fractalNoise\' baseFrequency=\'0.9\' numOctaves=\'4\' /%3E%3C/filter%3E%3Crect width=\'100%25\' height=\'100%25\' filter=\'url(%23noiseFilter)\' /%3E%3C/svg%3E")'}} />
        <div className="absolute inset-0 opacity-[0.08]" style={{backgroundImage: 'radial-gradient(circle at 2px 2px, rgba(255,255,255,0.4) 1px, transparent 0)', backgroundSize: '50px 50px'}} />
        <div className="absolute inset-0 opacity-5">
          <div className="absolute top-0 left-0 w-full h-full bg-gradient-to-br from-white via-transparent to-transparent" />
          <div className="absolute bottom-0 right-0 w-full h-full bg-gradient-to-tl from-white via-transparent to-transparent" />
        </div>

        <div className="max-w-6xl mx-auto relative z-10">
          <div className="text-center mb-12">
            <div className="inline-flex items-center gap-2 bg-white/20 backdrop-blur-xl px-4 py-2 rounded-full mb-4 border border-white/30 shadow-lg">
              {(() => {
                const BadgeIcon = impactSection?.badgeIcon && iconMap[impactSection.badgeIcon] ? iconMap[impactSection.badgeIcon] : TrendingUp;
                return <BadgeIcon className="w-4 h-4 text-[#E8B84D]" />;
              })()}
              <span className="text-sm font-semibold bg-gradient-to-r from-[#E8B84D] to-[#E87D3E] bg-clip-text text-transparent">
                {impactSection?.badgeText || 'Real-World Impact'}
              </span>
            </div>
            <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold mb-4">
              {impactSection?.title || 'How One Automotive Company Saved $350M'}
            </h2>
            <p className="text-base sm:text-lg text-white/90 max-w-3xl mx-auto leading-relaxed">
              {impactSection?.subtitle || 'A Fortune 500 automotive manufacturer deployed our 3D animation strategy across sales, training, and operations:'}
            </p>
          </div>
          
          <div className="grid sm:grid-cols-3 gap-6 max-w-5xl mx-auto mb-12">
            {(impactSection?.stats || [
              { id: '1', value: "$500K", label: "Investment in 3D Animation", description: "Strategic deployment across multiple departments", gradient: "from-[#4a487a] to-[#2F2E5C]", icon: "DollarSign", highlight: false },
              { id: '2', value: "100+", label: "Videos Produced", description: "Reused across sales, training, and operations", gradient: "from-[#E87D3E] to-[#E8B84D]", icon: "Target", highlight: false },
              { id: '3', value: "$350M", label: "Total Cost Savings", description: "Reduced training time, faster sales, fewer errors", highlight: true, gradient: "from-[#D74444] to-[#e85e5e]", icon: "TrendingUp" }
            ]).map((item, i) => {
              const StatIcon = item.icon && iconMap[item.icon] ? iconMap[item.icon] : TrendingUp;
              return (
                <div
                  key={item.id || i}
                  className={`group relative text-center p-8 rounded-2xl transition-all duration-300 hover:-translate-y-1 ${
                    item.highlight 
                      ? 'bg-gradient-to-br from-[#D74444] via-[#e85e5e] to-[#c13939] shadow-2xl shadow-[#D74444]/20' 
                      : 'bg-white/10 backdrop-blur-xl border border-white/30 shadow-xl hover:bg-white/15 hover:border-white/40'
                  }`}
                >
                  {!item.highlight && (
                    <div className={`absolute inset-0 bg-gradient-to-br ${item.gradient} opacity-0 group-hover:opacity-10 rounded-2xl transition-opacity duration-300`} />
                  )}
                  <div className="absolute inset-0 opacity-5" style={{backgroundImage: 'radial-gradient(circle at 2px 2px, white 1px, transparent 0)', backgroundSize: '30px 30px'}} />
                  <div className="relative z-10">
                    <div className={`w-14 h-14 mx-auto mb-4 rounded-xl flex items-center justify-center ${
                      item.highlight 
                        ? 'bg-white/20 backdrop-blur-md border border-white/40' 
                        : `bg-gradient-to-br ${item.gradient}`
                    } shadow-lg group-hover:scale-110 transition-transform duration-300`}>
                      <StatIcon className="w-6 h-6" />
                    </div>
                    <div className="text-4xl sm:text-5xl font-bold mb-2">{item.value}</div>
                    <div className="font-bold mb-2 text-base">{item.label}</div>
                    <p className="text-sm opacity-90 leading-relaxed">{item.description}</p>
                  </div>
                </div>
              );
            })}
          </div>

          <div className="text-center">
            <Link to={impactSection?.ctaLink || '/case-studies'}>
              <Button className="group relative overflow-hidden bg-white hover:bg-gray-50 text-[#2F2E5C] rounded-full px-8 h-12 gap-3 shadow-2xl shadow-white/10 hover:scale-105 transition-all duration-300 font-bold border-2 border-white/40">
                <div className="absolute inset-0 bg-gradient-to-r from-white via-[#E8B84D]/10 to-white opacity-0 group-hover:opacity-100 transition-opacity" />
                <span className="relative z-10 flex items-center gap-3">
                  {impactSection?.ctaText || 'View Full Case Study'}
                  <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
                </span>
              </Button>
            </Link>
          </div>
        </div>
      </section>
    ),
    'roi-calculator': <ROIMiniTeaserPremium key="roi-calculator" />,
    'how-we-work': <HowWeWorkSection key="how-we-work" />,
    'industry-expertise': <IndustryExpertiseSection key="industry-expertise" />,
    'blog-preview': <BlogPreview key="blog-preview" />,
    'video-planner': <VideoPlanner key="video-planner" />,
    'faq': <FAQSection key="faq" />,
  };

  const renderSections = () => {
    // Show shimmer skeletons while section order is loading
    if (orderLoading) {
      return <SectionSkeletons />;
    }

    if (sectionOrder.length > 0) {
      return sectionOrder
        .filter((section) => section.id !== 'hero')
        .map((section) => {
          if (!section.visible) return null;
          logger.log(`✅ [SECTION ORDER] Rendering section: ${section.name} (id: ${section.id})`);
          const component = sectionComponents[section.id];
          if (!component) {
            logger.warn(`⚠️ [SECTION ORDER] No component found for: ${section.id}`);
            return null;
          }
          return component;
        });
    }

    return Object.values(sectionComponents).filter(c => c !== null);
  };

  return (
    <>
      {/* SEO Meta Tags */}
      <Helmet>
        <title>{homepageSettings?.seo?.metaTitle || 'Professional 3D Animation & Explainer Videos | Creabiz Studio'}</title>
        <meta name="description" content={homepageSettings?.seo?.metaDescription || 'Transform complex ideas into engaging 3D animations and explainer videos for manufacturing, energy, and automotive industries.'} />
        {homepageSettings?.seo?.keywords && homepageSettings.seo.keywords.length > 0 && (
          <meta name="keywords" content={homepageSettings.seo.keywords.join(', ')} />
        )}
        <meta property="og:type" content="website" />
        <meta property="og:url" content={window.location.href} />
        <meta property="og:title" content={homepageSettings?.seo?.ogTitle || homepageSettings?.seo?.metaTitle || 'Professional 3D Animation & Explainer Videos | Creabiz Studio'} />
        <meta property="og:description" content={homepageSettings?.seo?.ogDescription || homepageSettings?.seo?.metaDescription || 'Transform complex ideas into engaging 3D animations and explainer videos for manufacturing, energy, and automotive industries.'} />
        {homepageSettings?.seo?.ogImage && (
          <meta property="og:image" content={homepageSettings.seo.ogImage} />
        )}
        <meta property="twitter:card" content="summary_large_image" />
        <meta property="twitter:url" content={window.location.href} />
        <meta property="twitter:title" content={homepageSettings?.seo?.ogTitle || homepageSettings?.seo?.metaTitle || 'Professional 3D Animation & Explainer Videos | Creabiz Studio'} />
        <meta property="twitter:description" content={homepageSettings?.seo?.ogDescription || homepageSettings?.seo?.metaDescription || 'Transform complex ideas into engaging 3D animations and explainer videos for manufacturing, energy, and automotive industries.'} />
        {homepageSettings?.seo?.ogImage && (
          <meta property="twitter:image" content={homepageSettings.seo.ogImage} />
        )}
      </Helmet>
      
      {/* Hero Section */}
      <section className="relative min-h-[600px] flex items-center px-4 sm:px-6 lg:px-8 py-16 sm:py-20 overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-gray-50 via-white to-gray-100" />
        <div className="absolute top-0 right-0 w-[600px] h-[600px] bg-gradient-to-br from-[#E8B84D]/10 via-[#E87D3E]/8 to-transparent rounded-full blur-3xl" />
        <div className="absolute bottom-0 left-0 w-[500px] h-[500px] bg-gradient-to-tr from-[#2F2E5C]/8 via-[#4A4878]/5 to-transparent rounded-full blur-3xl" />
        <div className="absolute inset-0 opacity-[0.015]" style={{backgroundImage: 'url("data:image/svg+xml,%3Csvg viewBox=\'0 0 400 400\' xmlns=\'http://www.w3.org/2000/svg\'%3E%3Cfilter id=\'noiseFilter\'%3E%3CfeTurbulence type=\'fractalNoise\' baseFrequency=\'0.9\' numOctaves=\'4\' /%3E%3C/filter%3E%3Crect width=\'100%25\' height=\'100%25\' filter=\'url(%23noiseFilter)\' /%3E%3C/svg%3E")'}} />
        
        <div className="max-w-6xl mx-auto w-full relative z-10">
          <div className="grid lg:grid-cols-2 gap-10 items-center">
            {/* Left Content */}
            <div>
              {heroLoading ? (
                <HeroSkeleton />
              ) : heroSection ? (
                <>
                  {(() => {
                    const BadgeIcon = iconMap[heroSection.badgeIcon] || Sparkles;
                    return (
                      <div className="inline-flex items-center gap-3 bg-white/60 backdrop-blur-xl px-5 py-2.5 rounded-full mb-6 border border-white/40 shadow-lg shadow-[#E8B84D]/5">
                        <BadgeIcon className="w-5 h-5 text-[#E8B84D]" />
                        <span className="text-sm font-semibold bg-gradient-to-r from-[#2F2E5C] via-[#E87D3E] to-[#E8B84D] bg-clip-text text-transparent">
                          {heroSection.badgeText}
                        </span>
                      </div>
                    );
                  })()}
                  
                  <h1 className="text-3xl sm:text-4xl lg:text-5xl font-bold mb-4 leading-tight text-[#2F2E5C]">
                    {heroSection.headlineLine1}<br />
                    {heroSection.headlineLine2}<br />
                    <span className="bg-gradient-to-r from-[#E87D3E] to-[#E8B84D] bg-clip-text text-transparent">
                      {heroSection.headlineLine3Gradient}
                    </span>
                  </h1>
                  <div 
                    className="text-base sm:text-lg text-gray-600 mb-6 leading-relaxed"
                    dangerouslySetInnerHTML={{ __html: heroSection.description }}
                  />
                  
                  <div className="flex flex-col sm:flex-row gap-3 mb-6">
                    {heroSection.cta1Visible && (
                      <Link to={heroSection.cta1Link}>
                        <Button className="relative overflow-hidden bg-gradient-to-r from-[#D74444] via-[#e85e5e] to-[#c13939] hover:from-[#c13939] hover:via-[#D74444] hover:to-[#e85e5e] text-white rounded-full px-6 h-11 gap-2 w-full sm:w-auto shadow-lg shadow-[#D74444]/20 before:absolute before:inset-0 before:bg-white/10 before:opacity-0 hover:before:opacity-100 before:transition-opacity">
                          <span className="relative z-10 flex items-center gap-2">
                            {heroSection.cta1Text}
                            <ArrowRight className="w-4 h-4" />
                          </span>
                        </Button>
                      </Link>
                    )}
                    {heroSection.cta2Visible && (
                      <Link to={heroSection.cta2Link}>
                        <Button variant="outline" className="relative overflow-hidden rounded-full px-6 h-11 gap-2 border-2 border-[#E87D3E] text-[#E87D3E] hover:bg-gradient-to-r hover:from-[#E87D3E] hover:to-[#E8B84D] hover:text-white hover:border-transparent w-full sm:w-auto transition-all duration-300 before:absolute before:inset-0 before:bg-gradient-to-r before:from-[#E87D3E] before:to-[#E8B84D] before:opacity-0 hover:before:opacity-100 before:transition-opacity">
                          <span className="relative z-10 flex items-center gap-2">
                            {heroSection.cta2Text}
                            <BarChart3 className="w-4 h-4" />
                          </span>
                        </Button>
                      </Link>
                    )}
                  </div>
                  
                  {heroSection.ratingVisible && (
                    <div className="flex items-center gap-4">
                      <div className="flex">
                        {Array.from({ length: Math.floor(heroSection.ratingValue) }).map((_, i) => (
                          <Star key={i} className="w-4 h-4 fill-[#E8B84D] text-[#E8B84D]" />
                        ))}
                      </div>
                      <span className="text-sm text-gray-600">
                        {heroSection.ratingValue} {heroSection.ratingText}
                      </span>
                    </div>
                  )}
                </>
              ) : (
                <div className="text-center py-8 text-gray-500">
                  Error loading hero content
                </div>
              )}
            </div>

            {/* Right Video Preview */}
            <div className="relative">
              {heroLoading ? (
                <VideoPlayerSkeleton />
              ) : heroVideoId && heroSection ? (
                <Suspense fallback={<VideoPlayerSkeleton />}>
                  <CustomVideoPlayer 
                    videoId={heroVideoId} 
                    autoplay={heroSection.videoAutoplay}
                    autoplayDelay={heroSection.videoDelay}
                  />
                </Suspense>
              ) : null}
              
              {/* Floating Stats */}
              {!heroLoading && heroSection && heroSection.statsVisible && (
                <>
                  <div className="absolute -bottom-3 -left-3 bg-white/80 backdrop-blur-xl rounded-2xl shadow-lg p-3 border border-white/40 z-40">
                    <div className="text-xl font-bold text-[#2F2E5C]">{heroSection.stat1Value}</div>
                    <div className="text-xs text-gray-600">{heroSection.stat1Label}</div>
                  </div>
                  <div className="absolute -top-3 -right-3 bg-gradient-to-br from-[#E8B84D] via-[#E87D3E] to-[#d4a03d] rounded-2xl shadow-lg p-3 text-white border border-white/20 z-40">
                    <div className="text-xl font-bold">{heroSection.stat2Value}</div>
                    <div className="text-xs text-white/90">{heroSection.stat2Label}</div>
                  </div>
                </>
              )}
            </div>
          </div>
        </div>
      </section>

      {/* Dynamic Sections */}
      {renderSections()}

      {/* Modals — lazy loaded, only mounted when needed */}
      <Suspense fallback={null}>
        <VideoPlannerModal
          isOpen={isPlannerModalOpen}
          onClose={() => setIsPlannerModalOpen(false)}
        />
      </Suspense>

      <Suspense fallback={null}>
        <VideoModal
          key={videoModalOpen ? currentVideo.url : 'closed'}
          isOpen={videoModalOpen}
          onClose={() => setVideoModalOpen(false)}
          videoUrl={currentVideo.url}
          title={currentVideo.title}
        />
      </Suspense>

      {pdfPreviewOpen && (
        <Suspense fallback={null}>
          <PDFDesignPreview onClose={() => setPdfPreviewOpen(false)} />
        </Suspense>
      )}
    </>
  );
}

export default HomePage;